import { BitkitLink } from '@bitrise/bitkit-v2';
import { Text } from '@chakra-ui/react/text';
import { useEffect, useState } from 'react';

import LoginButtonGroup from '@/components/buttons/LoginButtonGroup/LoginButtonGroup';
import LoginCard from '@/components/LoginCard/LoginCard';
import AuthPage from '@/components/page/AuthPage/AuthPage';
import { useLastUsedEmail } from '@/hooks/useLastUsedEmail';
import { setLoginAttempted } from '@/utils/loginTracking';

import SignInForm from './components/SignInForm';
import useSignInFlashMessage from './hooks/useSignInFlashMessage';
import SSOEmailForm from './SSOEmailForm';

type SignInFormStateType = 'selection' | 'email' | 'sso';

const SignInPage = ({ initialFormState }: { initialFormState?: SignInFormStateType }) => {
  const [formState, setFormState] = useState<SignInFormStateType>(initialFormState ?? 'selection');
  const [rememberMe, setRememberMe] = useState(false);
  const [lastSSOEmail, setLastSSOEmail] = useLastUsedEmail();

  useSignInFlashMessage();

  const ssoButtonProps = {
    onClick: () => setFormState('sso'),
  };

  useEffect(() => {
    setLoginAttempted();

    if (lastSSOEmail) {
      setFormState('sso');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount

  const backButtonProps = {
    onBackButtonClick: () => {
      setFormState('selection');
      setLastSSOEmail('');
    },
    backButtonLabel: 'Back to all sign in methods',
  };

  return (
    <AuthPage
      title="Log in to your account"
      bottomMessage={
        <>
          Don&lsquo;t have an account?{' '}
          <BitkitLink href="/users/sign_up" textDecoration="underline">
            Sign up
          </BitkitLink>
        </>
      }
    >
      {formState === 'selection' && (
        <LoginCard variant="transparent">
          <LoginButtonGroup
            onEmailSelect={() => setFormState('email')}
            ssoButtonProps={ssoButtonProps}
            signUpMode={false}
            rememberMe={rememberMe}
          />
        </LoginCard>
      )}
      {formState === 'email' && (
        <LoginCard title="Log in with email" {...backButtonProps}>
          <SignInForm rememberMe={rememberMe} onRememberMeChanged={setRememberMe} />
        </LoginCard>
      )}
      {formState === 'sso' && (
        <LoginCard title="Sign in with SSO" subtitle="Please sign in using your SSO credentials." {...backButtonProps}>
          <SSOEmailForm rememberMe={rememberMe} onRememberMeChanged={setRememberMe} />
        </LoginCard>
      )}
      {formState === 'email' && (
        <Text textStyle="body/lg/regular" color="text/on-color">
          <BitkitLink href="/users/forgot-password" textAlign="center">
            Forgot your password?
          </BitkitLink>
        </Text>
      )}
    </AuthPage>
  );
};

export default SignInPage;
